import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Button, Group, Text } from '@mantine/core';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import useAthletes from 'hooks/athlete/useAthletes';
import { useParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteList } from 'types/generated';
import { formatPhoneNumber } from 'utils/formatters';

const AdminAthletesTableContainer = ({
  searchFilters,
}: {
  searchFilters: {
    searchTerm: string;
    isArrangeByLastName: boolean;
    athleteStatus: string;
    loginEnabled: string;
  };
}): JSX.Element => {
  const [ordering, setOrdering] = useState('firstName');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<AthleteList[]>([]);
  const params = useParams<{ [key: string]: string }>();

  const { impersonate } = useAuth();

  const orderingValue =
    ordering === 'firstName' && searchFilters.isArrangeByLastName ? 'lastName' : ordering;
  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthletes({
      ...searchFilters,
      ordering: ascending ? orderingValue : `-${orderingValue}`,
      gymId: params.gymId,
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<AthleteList>) => (
        <Group noWrap spacing='xs'>
          <Text weight='bold'>{row.original.fullName}</Text>
          {row.original.primaryAccount && (
            <Badge color='kiloBlue' variant='filled' size='xs' radius='md'>
              Sub-account
            </Badge>
          )}
        </Group>
      ),
    },
    {
      Header: 'Athlete Id',
      accessor: 'athleteId' as const,
      Cell: ({ row }: CellProps<AthleteList>) => `${row.original.athleteId}`,
    },
    {
      Header: 'Status',
      accessor: 'athleteStatus' as const,
      Cell: ({ value }: CellProps<AthleteList>) => <AthleteStatusBadge status={value} />,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      Cell: ({ value }: CellProps<AthleteList>) => formatPhoneNumber(value),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<AthleteList>) => (
        <Button compact variant='outline' onClick={() => impersonate(row.original.profile)}>
          Impersonate
        </Button>
      ),
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'athleteStatus', 'loginEnabled']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
      />
    </>
  );
};

export default AdminAthletesTableContainer;
